import { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useApp } from '../lib/AppContext';
import Button from '../components/Button';
import Header from '../components/Header';
import { supabase } from '../lib/supabaseClient';

type DuplicateAccountState = 'verified' | 'unverified' | null;
type AuthMode = 'signin' | 'signup' | 'reset';

// Helper to get initial mode from URL
const getInitialMode = (): AuthMode => {
  const hash = window.location.hash;
  if (hash.includes('?')) {
    const params = new URLSearchParams(hash.split('?')[1]);
    const urlMode = params.get('mode');
    if (urlMode === 'reset') {
      return 'reset';
    }
  }
  return 'signup';
};

// Helper to get prefilled email from session storage
const getPrefillEmail = (): string => {
  const storedEmail = sessionStorage.getItem('reset_password_email');
  if (storedEmail) {
    sessionStorage.removeItem('reset_password_email');
    return storedEmail;
  }
  return '';
};

export default function Auth() {
  const { signIn, signUp } = useAuth();
  const { navigateTo, navigateBack, showToast, restorationState, clearRestorationState } = useApp();
  const [mode, setMode] = useState<AuthMode>(getInitialMode());
  const [email, setEmail] = useState(getPrefillEmail());
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [showUnverifiedWarning, setShowUnverifiedWarning] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [duplicateAccountState, setDuplicateAccountState] = useState<DuplicateAccountState>(null);
  const [duplicateAccountEmail, setDuplicateAccountEmail] = useState('');
  const [showInlineAlert, setShowInlineAlert] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);

  // Set initial mode from restoration state if provided
  useEffect(() => {
    if (restorationState?.authMode) {
      setMode(restorationState.authMode);
      clearRestorationState();
    }
  }, [restorationState, clearRestorationState]);

  // Clean up URL parameter after initial mode has been set
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('?mode=reset')) {
      // Clean up the URL parameter to keep it clean
      window.history.replaceState(null, '', '#/auth');
    }
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      showToast('Please enter your email');
      return;
    }

    // Reset mode only needs email
    if (mode === 'reset') {
      await handlePasswordResetRequest();
      return;
    }

    if (!password.trim()) {
      showToast('Please enter your password');
      return;
    }

    if (password.length < 6) {
      showToast('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    if (mode === 'signup') {
      // PRE-CHECK: Check if email already exists before calling signUp
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('check_user_email_confirmed', {
          user_email: email.trim().toLowerCase()
        });

        console.log('[DEBUG] Pre-check RPC result:', {
          data: rpcData,
          error: rpcError,
        });

        if (rpcError) {
          console.warn('[DEBUG] RPC error during pre-check:', rpcError);
          // Continue with signup if RPC fails
        } else if (rpcData && typeof rpcData === 'object') {
          const exists = rpcData.exists;
          const confirmed = rpcData.confirmed;

          console.log('[DEBUG] Pre-check parsed:', { exists, confirmed });

          if (exists === true && confirmed === true) {
            // User exists and is verified - skip signUp, show inline alert
            console.log('[DEBUG] Existing verified account detected - showing inline alert');
            setLoading(false);
            setDuplicateAccountEmail(email);
            setDuplicateAccountState('verified');
            setShowInlineAlert(true);
            return;
          } else if (exists === true && confirmed === false) {
            // User exists but not verified - skip signUp, show inline alert
            console.log('[DEBUG] Existing unverified account detected - showing inline alert');
            setLoading(false);
            setDuplicateAccountEmail(email);
            setDuplicateAccountState('unverified');
            setShowInlineAlert(true);
            return;
          }
          // If exists === false, continue with signUp below
        }
      } catch (err) {
        console.warn('[DEBUG] Exception during pre-check, continuing with signUp:', err);
      }

      // PROCEED WITH SIGNUP (only if user doesn't exist)
      console.log('[DEBUG] Calling signUp for new email');
      const result = await signUp(email, password);

      // Debug logging - show raw Supabase response
      console.log('[DEBUG] signUp raw response:', {
        success: result.success,
        error: result.error,
        'data.user': result.data?.user,
        'data.session': result.data?.session,
        'data.user?.identities': result.data?.user?.identities,
      });

      setLoading(false);

      if (result.error) {
        // Error occurred during signup
        console.error('[DEBUG] Signup error:', result.error);
        showToast(result.error);
      } else {
        // No error = successful signup (even if user/session are null due to email confirmation)
        console.log('[DEBUG] Signup successful, showing verification panel');
        setVerificationEmail(email);
        setShowVerificationPanel(true);
      }
    } else {
      // Sign in mode - check for email verification
      const result = await signIn(email, password);
      setLoading(false);

      if (result.success) {
        showToast('Signed in successfully!');
        navigateBack();
      } else {
        // Check if account is deactivated
        if (result.error?.toLowerCase().includes('deactivated')) {
          setIsDeactivated(true);
        }
        // Check if error is due to unverified email
        else if (result.error?.toLowerCase().includes('email not confirmed') ||
            result.error?.toLowerCase().includes('email') && result.error?.toLowerCase().includes('confirm')) {
          setUnverifiedEmail(email);
          setShowUnverifiedWarning(true);
        } else {
          showToast(result.error || 'Authentication failed');
        }
      }
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: verificationEmail,
      });

      if (error) {
        showToast(error.message);
      } else {
        showToast('Verification email sent. Please check your inbox.');
      }
    } catch (error) {
      showToast('Failed to resend verification email');
    }
    setLoading(false);
  };

  const handleResendForUnverified = async () => {
    if (!unverifiedEmail) return;

    setResendingVerification(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: unverifiedEmail,
      });

      if (error) {
        showToast("Couldn't resend email. Please try again.");
        setResendingVerification(false);
      } else {
        showToast('Verification email sent. Please check your inbox.');
        setResendingVerification(false);
        // Start 30 second cooldown
        setResendCooldown(30);
      }
    } catch (error) {
      showToast("Couldn't resend email. Please try again.");
      setResendingVerification(false);
    }
  };

  const handleBackToSignIn = () => {
    setShowVerificationPanel(false);
    setMode('signin');
    setEmail('');
    setPassword('');
  };

  const handleResendForDuplicateUnverified = async () => {
    if (!duplicateAccountEmail) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: duplicateAccountEmail,
      });

      if (error) {
        showToast(error.message);
      } else {
        showToast('Verification email sent again.');
      }
    } catch (error) {
      showToast('Failed to resend verification email');
    }
    setLoading(false);
  };

  const handleDismissDuplicateState = () => {
    setDuplicateAccountState(null);
    setDuplicateAccountEmail('');
    setShowInlineAlert(false);
  };

  const handleSwitchToSignIn = () => {
    setDuplicateAccountState(null);
    setDuplicateAccountEmail('');
    setShowInlineAlert(false);
    setMode('signin');
    // Keep the email filled in
  };

  const handleForgotPassword = () => {
    setMode('reset');
    setPassword('');
    setShowInlineAlert(false);
    setDuplicateAccountState(null);
  };

  const handlePasswordResetRequest = async () => {
    if (!email.trim()) {
      showToast('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/#/reset-password`,
      });

      if (error) throw error;

      showToast('Password reset link sent! Check your email.');
      setVerificationEmail(email);
      setShowVerificationPanel(true);
    } catch (error: any) {
      console.error('Password reset error:', error);
      showToast(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleInlineResendVerification = async () => {
    if (!duplicateAccountEmail) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: duplicateAccountEmail,
      });

      if (error) {
        showToast(error.message);
      } else {
        showToast('Verification email sent. Please check your inbox.');
      }
    } catch (error) {
      showToast('Failed to resend verification email');
    }
    setLoading(false);
  };

  // Reset warning and inline alert when switching modes or changing email
  useEffect(() => {
    setShowUnverifiedWarning(false);
    setResendCooldown(0);
    setDuplicateAccountState(null);
    setShowInlineAlert(false);
    setIsDeactivated(false);
  }, [mode, email]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle verification errors on auth page
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('error')) {
      const errorParam = new URLSearchParams(hash.substring(1)).get('error_description');
      if (errorParam) {
        showToast('Verification link expired or invalid.');
        setShowUnverifiedWarning(true);
      }
    }
  }, [showToast]);

  // Inline alerts are now shown within the form, not as separate screens

  // Show verification panel after successful signup or password reset request
  if (showVerificationPanel) {
    const isPasswordReset = mode === 'reset';

    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          {/* Verification Panel */}
          <div className="bg-gradient-to-br from-[#181818] to-[#1a1a1a] rounded-3xl border border-[#40E0D0]/20 shadow-2xl shadow-black/50 p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[#40E0D0]/10 flex items-center justify-center">
                <Mail className="w-10 h-10 text-[#40E0D0]" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-[26px] font-semibold text-[#F5F5F5] mb-4 text-center leading-tight">
              {isPasswordReset ? 'Check your email' : 'Check your email'}
            </h2>

            {/* Description */}
            <div className="mb-8">
              <p className="text-[15px] text-[#C5C5C5] leading-relaxed text-center mb-3">
                We've sent a {isPasswordReset ? 'password reset' : 'confirmation'} link to{' '}
                <span className="text-[#40E0D0] font-medium">{verificationEmail}</span>.
              </p>
              <p className="text-[15px] text-[#C5C5C5] leading-relaxed text-center">
                {isPasswordReset
                  ? 'Click the link in the email to reset your password.'
                  : 'Please check your inbox and click the link to activate your account.'}
              </p>
            </div>

            {/* Resend button - only for verification, not password reset */}
            {!isPasswordReset && (
              <Button
                onClick={handleResendVerification}
                disabled={loading}
                variant="secondary"
                fullWidth
                className="mb-4"
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </Button>
            )}

            {/* Already verified link - only for verification */}
            {!isPasswordReset && (
              <button
                onClick={handleBackToSignIn}
                className="w-full text-[14px] text-[#A0A0A0] hover:text-[#B5B5B5] transition-colors py-3 mb-2"
                disabled={loading}
              >
                Already verified? Sign in
              </button>
            )}

            {/* Secondary action: Return to app or Back to Sign In */}
            <button
              onClick={() => {
                if (isPasswordReset) {
                  // Clean URL and navigate to home
                  window.history.replaceState(null, '', '/');
                  navigateTo('home');
                } else {
                  handleBackToSignIn();
                }
              }}
              className="w-full text-[14px] text-[#C5C5C5] hover:text-[#F5F5F5] transition-colors py-3"
              disabled={loading}
            >
              {isPasswordReset ? 'Return to app' : 'Back to Sign In'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show signin/signup/reset form
  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Minimal top nav with back arrow - only for signin/signup, not reset */}
      {mode !== 'reset' && (
        <div className="sticky top-0 z-10 bg-[#111111] border-b border-[#282828]">
          <div className="px-6 py-4 flex items-center gap-4">
            <button
              onClick={() => {
                navigateBack();
              }}
              className="flex-shrink-0 flex items-center gap-2 text-[#F5F5F5] hover:text-[#40E0D0] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-[20px] font-semibold text-[#F5F5F5]" style={{ letterSpacing: '-0.005em' }}>
              {mode === 'signup' ? 'Create Account' : 'Sign In'}
            </h1>
          </div>
        </div>
      )}

      <div className={`${mode === 'reset' ? 'min-h-screen' : ''} flex items-center justify-center px-6 ${mode !== 'reset' ? 'py-12' : ''}`}>
        <div className="max-w-md w-full">
          {/* Gradient Card Container */}
          <div className="bg-gradient-to-br from-[#181818] to-[#1a1a1a] rounded-3xl border border-[#40E0D0]/20 shadow-2xl shadow-black/50 p-8">
            {/* Main heading inside card - always visible for all modes */}
            <div className="mb-6">
              <h2 className="text-[26px] font-semibold text-[#F5F5F5] mb-3 leading-tight">
                {mode === 'reset' ? 'Reset Your Password' : mode === 'signup' ? 'Create Your Account' : 'Welcome Back'}
              </h2>
              <p className="text-[15px] text-[#C5C5C5] leading-relaxed">
                {mode === 'reset'
                  ? "Enter your email and we'll send you a reset link."
                  : mode === 'signup'
                  ? 'Save stories and see patterns over time.'
                  : 'Pick up where you left off.'}
              </p>
            </div>

          {/* Deactivated Account Notice */}
          {isDeactivated && mode === 'signin' && (
            <div className="mb-6 p-4 bg-[#1a1a1a] border border-[#40E0D0]/30 rounded-2xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#40E0D0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertCircle className="w-5 h-5 text-[#40E0D0]" />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-[#F5F5F5] mb-1.5">
                    Your account is deactivated
                  </h3>
                  <p className="text-[13px] text-[#C5C5C5] leading-relaxed">
                    Contact support if you need access restored.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Unverified Email Warning */}
          {showUnverifiedWarning && mode === 'signin' && !isDeactivated && (
            <div className="mb-6 p-4 bg-[#2EC4B6]/[0.08] border border-[#2EC4B6]/25 rounded-2xl">
              <p className="text-[14px] text-[#C5C5C5] leading-relaxed mb-3">
                Your email hasn't been verified yet.<br />
                Check your inbox for the confirmation link.
              </p>
              <Button
                onClick={handleResendForUnverified}
                disabled={resendingVerification || resendCooldown > 0}
                variant="secondary"
                fullWidth
              >
                {resendingVerification
                  ? 'Sending...'
                  : resendCooldown > 0
                    ? `Resend again in ${resendCooldown}s`
                    : 'Resend Verification Email'}
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-[13px] font-medium text-[#B0B0B0] mb-2">
                Email address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#4a4a4a] rounded-xl text-[15px] text-[#F5F5F5] placeholder-[#888888] focus:outline-none focus:border-[#40E0D0] focus:bg-[#141414] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || resendingVerification || isDeactivated}
              />
            </div>

            {/* Password field - hidden in reset mode */}
            {mode !== 'reset' && (
              <div>
                <label htmlFor="password" className="block text-[13px] font-medium text-[#B0B0B0] mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#4a4a4a] rounded-xl text-[15px] text-[#F5F5F5] placeholder-[#888888] focus:outline-none focus:border-[#40E0D0] focus:bg-[#141414] transition-all pr-12 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading || resendingVerification || isDeactivated}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                    disabled={loading || resendingVerification}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Inline message for verified account */}
            {showInlineAlert && mode === 'signup' && duplicateAccountState === 'verified' && (
              <div className="mt-3 animate-in fade-in duration-200">
                <p className="text-[13px] text-[#C5C5C5] leading-relaxed">
                  An account already exists with this email.
                </p>
              </div>
            )}

            {/* Inline message for unverified account */}
            {showInlineAlert && mode === 'signup' && duplicateAccountState === 'unverified' && (
              <div className="mt-3 animate-in fade-in duration-200">
                <p className="text-[13px] text-[#C5C5C5] leading-relaxed">
                  An account already exists with this email.
                </p>
                <p className="text-[13px] text-[#C5C5C5] mt-2">
                  Please check your inbox to activate your account.
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || resendingVerification || isDeactivated}
              variant="primary"
              fullWidth
              className="mt-7"
              onClick={showInlineAlert && mode === 'signup' && duplicateAccountState === 'verified' ? handleSwitchToSignIn : undefined}
            >
              {loading
                ? (mode === 'reset' ? 'Sending...' : mode === 'signup' ? 'Creating...' : 'Signing in...')
                : (mode === 'reset' ? 'Send Reset Link' : mode === 'signup' && showInlineAlert && duplicateAccountState === 'verified' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Sign In')}
            </Button>
          </form>

          {/* Forgot password link - show in signin mode or when duplicate verified account */}
          {mode !== 'reset' && (mode === 'signin' || (showInlineAlert && mode === 'signup' && duplicateAccountState === 'verified')) && (
            <div className="mt-4">
              <button
                onClick={handleForgotPassword}
                disabled={loading}
                className="w-full text-[14px] text-[#A0A0A0] hover:text-[#B5B5B5] transition-colors py-2 disabled:opacity-50"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Resend verification button (shown only for unverified duplicate) */}
          {showInlineAlert && mode === 'signup' && duplicateAccountState === 'unverified' && (
            <div className="mt-4 space-y-2">
              <Button
                onClick={handleInlineResendVerification}
                disabled={loading}
                variant="secondary"
                fullWidth
              >
                {loading ? 'Sending...' : 'Resend verification email'}
              </Button>
            </div>
          )}

          {/* Return to app from reset mode */}
          {mode === 'reset' && (
            <div className="mt-6">
              <button
                onClick={() => {
                  // Clean URL and navigate to home
                  window.history.replaceState(null, '', '/');
                  navigateTo('home');
                }}
                disabled={loading}
                className="w-full text-[14px] text-[#C5C5C5] hover:text-[#F5F5F5] transition-colors py-3"
              >
                Return to app
              </button>
            </div>
          )}

          {/* Sign In Instead / Create Account button (hidden when verified duplicate is shown or in reset mode) */}
          {mode !== 'reset' && !(showInlineAlert && mode === 'signup' && duplicateAccountState === 'verified') && (
            <div className="mt-6">
              <Button
                onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
                disabled={loading || resendingVerification}
                variant="secondary"
                fullWidth
              >
                {mode === 'signup'
                  ? 'Sign In Instead'
                  : 'Create Account'}
              </Button>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
